import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import AuthLoadingScreen from '../components/AuthLoadingScreen';

interface BootLoadingApi {
  show: (message: ReactNode) => void;
  hide: () => void;
}

const BootLoadingContext = createContext<BootLoadingApi | undefined>(undefined);

/**
 * Hosts a single AuthLoadingScreen instance shared by every bootstrap gate
 * (auth config, session verification) so switching between them only swaps
 * the message instead of unmounting/remounting the screen and replaying its
 * entrance animation.
 */
export function BootLoadingProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<ReactNode | null>(null);

  const api = useMemo<BootLoadingApi>(
    () => ({
      show: msg => setMessage(msg),
      hide: () => setMessage(null),
    }),
    []
  );

  return (
    <BootLoadingContext.Provider value={api}>
      {children}
      {message !== null && <AuthLoadingScreen message={message} />}
    </BootLoadingContext.Provider>
  );
}

export function useBootLoading() {
  const ctx = useContext(BootLoadingContext);
  if (!ctx) throw new Error('useBootLoading must be used within a BootLoadingProvider');
  return ctx;
}
